import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import Taskbar from "@/components/win95/Taskbar";
import Window from "@/components/win95/Window";
import Titlebar from "@/components/win95/Titlebar";
import ExplorerBar from "@/components/win95/ExplorerBar";
import AuthModal from "@/components/auth/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { useEditions } from "@/hooks/useEditions";
import { useMembers } from "@/hooks/useMembers";
import type { DbEdition, DbMember } from "@/types/database";

/* ────────── Scroll-reveal hook ────────── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          io.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return ref;
}

function Reveal({
  children,
  className = "reveal",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useReveal();
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

/* ────────── Hero Particles ────────── */
function HeroParticles() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const colors = ["#c084fc", "#a855d4", "#7b3fa0", "#ffffff"];
    for (let i = 0; i < 18; i++) {
      const p = document.createElement("div");
      p.className = "particle";
      const sz = Math.random() * 4 + 2;
      p.style.cssText = `width:${sz}px;height:${sz}px;left:${Math.random() * 100}%;animation-duration:${Math.random() * 12 + 8}s;animation-delay:${Math.random() * 10}s;opacity:0;`;
      p.style.background = colors[Math.floor(Math.random() * 4)];
      container.appendChild(p);
    }
  }, []);

  return <div ref={ref} className="hero-particles" />;
}

/* ────────── Hero Section ────────── */
function HeroSection() {
  return (
    <section className="hero">
      <div className="hero-bg" />
      <HeroParticles />
      <img src="/Logo2.png" alt="" className="hero-logo-big" />
      <div className="hero-content">
        <div className="hero-label">ENGENHARIA ELÉTRICA — UFES</div>
        <div className="hero-title">
          Conheça
          <br />
          mais sobre
          <br />a <span>Pro_Gramming</span>
        </div>
        <div className="hero-sub">
          Democratizando o aprendizado de Python desde 2023. Oficinas, tutoria e
          comunidade para todos os níveis.
        </div>
        <div className="hero-cta">
          <a href="#sobre" className="btn btn-primary">
            [ SAIBA MAIS ]
          </a>
          <Link to="/tutor" className="btn btn-secondary">
            ▶ TUTOR ONLINE
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ────────── Sobre Section ────────── */
function SobreSection() {
  return (
    <section className="section" id="sobre" style={{ position: "relative", overflow: "hidden" }}>
      <img
        src="/Logo2.png"
        alt=""
        className="section-float-logo"
        style={{ width: 300, bottom: -20, right: -30, animationDelay: "-3s" }}
      />
      <div className="section-anchor" />
      <div className="section-win-wrap" style={{ position: "relative", zIndex: 1 }}>
        <Reveal>
          <div className="section-heading">SOBRE O PROJETO</div>
        </Reveal>
        <Reveal className="reveal delay-1">
          <Window>
            <Titlebar title="Pro_Gramming — Sobre.txt" icon="💡" />
            <ExplorerBar items={["Arquivo", "Editar", "Exibir", "Ajuda"]} />
            <div className="sobre-grid">
              <div className="sobre-text">
                <h3>// O QUE É?</h3>
                <p>
                  A <strong>Pro_Gramming</strong> é uma iniciativa do <a href="https://peteletrica.ufes.br/" target="_blank" rel="noopener noreferrer">
                  PET Elétrica UFES</a> criada com o objetivo de democratizar o ensino de programação 
                  para estudantes de todos os cursos, sem a necessidade de conhecimentos prévios.
                </p>
                <p>
                  Por meio de oficinas práticas, mentorias e materiais de apoio, 
                  o projeto transforma o aprendizado de Python em uma experiência acessível, 
                  colaborativa e dinâmica, incentivando a criatividade, a resolução de problemas 
                  e o aprendizado em comunidade.
                </p>
                <div className="objetivos-list">
                  <h3 style={{ marginBottom: 16 }}>// OBJETIVOS</h3>
                  {[
                    "Ensinar lógica de programação e Python do zero para iniciantes",
                    "Reduzir barreiras de entrada no universo da programação",
                    "Fomentar a cultura de aprendizado colaborativo no campus",
                    "Produzir material didático aberto e acessível para todos",
                    "Criar uma comunidade engajada ao redor da programação criativa",
                  ].map((text, i) => (
                    <div className="obj-item" key={i}>
                      <div className="obj-bullet">
                        <span>{String(i + 1).padStart(2, "0")}</span>
                      </div>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sobre-stats">
                <h3>// RESULTADOS</h3>
                {[
                  { num: "3", label: "EDIÇÕES REALIZADAS\n(2023, 2024 E 2025)" },
                  { num: "120+", label: "PARTICIPANTES\nNO TOTAL" },
                  { num: "40+", label: "HORAS DE CONTEÚDO\nMINISTRADO" },
                  { num: "8", label: "MÓDULOS PEDAGÓGICOS\nDESENVOLVIDOS" },
                  { num: "95%", label: "TAXA DE SATISFAÇÃO\nDOS ALUNOS" },
                ].map(({ num, label }) => (
                  <div className="stat-item" key={num}>
                    <div className="stat-num">{num}</div>
                    <div
                      className="stat-label"
                      dangerouslySetInnerHTML={{ __html: label.replace("\n", "<br>") }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Window>
        </Reveal>
      </div>
    </section>
  );
}

/* ────────── Folder SVG ────────── */
function FolderIcon({ fill1, fill2, fill3 }: { fill1: string; fill2: string; fill3: string }) {
  return (
    <svg className="folder-icon-svg" viewBox="0 0 64 52" fill="none">
      <rect x="0" y="10" width="64" height="42" rx="2" fill={fill1} />
      <rect x="0" y="6" width="26" height="8" rx="2" fill={fill2} />
      <rect x="0" y="14" width="64" height="38" rx="2" fill={fill3} />
      <rect x="4" y="18" width="56" height="4" rx="1" fill="rgba(255,255,255,0.3)" />
      <rect x="4" y="26" width="56" height="3" rx="1" fill="rgba(74,29,122,0.25)" />
    </svg>
  );
}

const folderColors: Record<number, [string, string, string]> = {
  2023: ["#d4a017", "#e8b820", "#f0c030"],
  2024: ["#d4a017", "#e8b820", "#f0c030"],
  2025: ["#d4a017", "#e8b820", "#f0c030"],
};

/* ────────── Edition Popup ────────── */
function EditionPopup({
  edition,
  isOpen,
  onClose,
}: {
  edition: DbEdition;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;

  const ordinals = ["1ª", "2ª", "3ª", "4ª", "5ª"];
  const ordinal = ordinals[edition.edition_number - 1] ?? `${edition.edition_number}ª`;

  return (
    <div className="edicao-popup-overlay" onClick={onClose}>
      <div className="edicao-popup" onClick={(e) => e.stopPropagation()}>
        <div className="edicao-popup-titlebar">
          <div className="edicao-popup-title">
            📁 C:\Pro_Gramming\{edition.year} — Explorer
          </div>
          <div className="win-controls">
            <div className="win-btn">_</div>
            <div className="win-btn">□</div>
            <div className="popup-close-btn" onClick={onClose}>
              ✕
            </div>
          </div>
        </div>
        <div className="popup-explorer-bar">
          <span>Arquivo</span>
          <span>Editar</span>
          <span>Exibir</span>
          <span>Ajuda</span>
          <span style={{ marginLeft: "auto", color: "#666" }}>
            C:\Pro_Gramming\{edition.year}\
          </span>
        </div>
        <div className="popup-body">
          <div className="popup-sidebar">
            <div className="popup-sidebar-logo">
              <img src="/Logo2.png" alt={String(edition.year)} />
            </div>
            <div className="popup-year-badge">{edition.year}</div>
            <div className="popup-edition-num">
              {ordinal} EDIÇÃO
              <br />
              {edition.subtitle.toUpperCase()}
            </div>
            <div className="popup-tags">
              {edition.tags.map((tag) => (
                <span className="tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
            {edition.media_url && (
              <a href={edition.media_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                <div className="file-icon" >
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="2" y="1" width="18" height="22" rx="1" fill="#6b50c0" />
                    <rect x="2" y="1" width="18" height="22" rx="1" stroke="#000" strokeWidth="1.5" />
                    <polygon points="15,1 20,6 15,6" fill="#9470e0" stroke="#000" strokeWidth="1" />
                    <circle cx="22" cy="20" r="6" fill="#c0c0c0" stroke="#000" strokeWidth="1.5" />
                    <ellipse cx="22" cy="20" rx="2" ry="2" fill="#808080" stroke="#000" strokeWidth="1" />
                    <circle cx="22" cy="20" r="1" fill="#4a1d7a" />
                  </svg>
                  {edition.media_label ?? "media.mp4"}
                </div>
              </a>
            )}
          </div>
          <div className="popup-main">
            <h4>// {ordinal} EDIÇÃO</h4>
            {edition.description.split("\n\n").map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {edition.stats.length > 0 && (
              <div className="popup-stats-row">
                {edition.stats.map((s, i) => (
                  <div className="popup-stat" key={i}>
                    <div className="popup-stat-num">{s.value}</div>
                    <div
                      className="popup-stat-label"
                      dangerouslySetInnerHTML={{
                        __html: s.label.replace("\n", "<br>"),
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="popup-statusbar">
          <span>
            {edition.is_current ? "Edição em andamento" : "1 objeto(s) selecionado(s)"}
          </span>
          <span>{edition.is_current ? "Fique de olho no Instagram!" : `Ano: ${edition.year}`}</span>
        </div>
      </div>
    </div>
  );
}

/* ────────── Edicoes Section ────────── */
function EdicoesSection() {
  const { editions, isLoading } = useEditions();
  const [openYear, setOpenYear] = useState<number | null>(null);

  const handleClose = useCallback(() => setOpenYear(null), []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenYear(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openEdition = editions.find((e) => e.year === openYear);

  return (
    <section className="section" id="edicoes" style={{ position: "relative", overflow: "hidden" }}>
      <img
        src="/Logo2.png"
        alt=""
        className="section-float-logo"
        style={{ width: 250, top: 40, left: -40, animationDelay: "-5s" }}
      />
      <div className="section-anchor" />
      <div className="section-win-wrap" style={{ position: "relative", zIndex: 1 }}>
        <Reveal>
          <div className="section-heading">EDIÇÕES</div>
        </Reveal>
        <Reveal className="reveal delay-1">
          <Window>
            <Titlebar title="C:\Pro_Gramming\ — Explorer" icon="📁" />
            <ExplorerBar
              items={["Arquivo", "Editar", "Pesquisar", "Ajuda"]}
              rightContent={
                <span style={{ marginLeft: "auto", color: "#666" }}>
                  C:\Pro_Gramming\
                </span>
              }
            />
            <div className="edicoes-desktop">
              <div style={{ fontFamily: "var(--text-mono)", fontSize: 13, color: "#555", padding: "0 4px" }}>
                {isLoading ? "Carregando..." : "Dê duplo clique em uma pasta para abrir"}
              </div>
              <div className="edicoes-folders-row">
                {editions.map((ed) => {
                  const colors = folderColors[ed.year] ?? ["#d4a017", "#e8b820", "#f0c030"];
                  return (
                    <div
                      className="folder-item"
                      key={ed.id}
                      onDoubleClick={() => setOpenYear(ed.year)}
                      title={`Abrir ${ed.year}`}
                    >
                      <FolderIcon fill1={colors[0]} fill2={colors[1]} fill3={colors[2]} />
                      <div className="folder-label">
                        Pro_Gramming
                        <br />— {ed.year}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontFamily: "var(--text-mono)", fontSize: 12, color: "#777", padding: 4 }}>
                {editions.length} objeto(s)
              </div>
            </div>
          </Window>
        </Reveal>
      </div>

      {openEdition && (
        <EditionPopup edition={openEdition} isOpen={true} onClose={handleClose} />
      )}
    </section>
  );
}

/* ────────── Member Card ────────── */
function MemberCard({ member }: { member: DbMember }) {
  const isFounder = member.category === "founder";
  const card = (
    <div className={`membro-card${isFounder ? " founder" : ""}`}>
      <div className="membro-avatar">
        {member.avatar_url ? (
          <img
            src={member.avatar_url}
            alt={member.display_name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          member.initials
        )}
      </div>
      <div className="membro-name">{member.display_name.toUpperCase()}</div>
      <div className="membro-role">{member.role_label}</div>
      {isFounder && <div className="founder-badge">★ FOUNDER</div>}
      {member.github_handle && (
        <div className="gh-link">github/{member.github_handle}</div>
      )}
    </div>
  );

  if (member.github_url) {
    return (
      <a href={member.github_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
        {card}
      </a>
    );
  }

  return card;
}

/* ────────── Membros Section ────────── */
function MembrosSection() {
  const { founders, active, alumni, isLoading } = useMembers();

  return (
    <section className="section" id="membros" style={{ position: "relative", overflow: "hidden" }}>
      <img
        src="/Logo2.png"
        alt=""
        className="section-float-logo"
        style={{ width: 230, top: "50%", right: -30, transform: "translateY(-50%)", animationDelay: "-1s" }}
      />
      <div className="section-anchor" />
      <div className="section-win-wrap" style={{ position: "relative", zIndex: 1 }}>
        <Reveal>
          <div className="section-heading">MEMBROS</div>
        </Reveal>
        <Reveal className="reveal delay-1">
          <Window>
            <Titlebar title="Pro_Gramming — Membros.exe" icon="👥" />
            <ExplorerBar items={["Arquivo", "Editar", "Pesquisar", "Ajuda"]} />
            <div className="membros-inner">
              {isLoading ? (
                <div style={{ padding: 20, textAlign: "center", color: "#888" }}>
                  Carregando membros...
                </div>
              ) : (
                <>
                  
                  {active.length > 0 && (
                    <>
                      <div className="membros-section-title">MEMBROS ATIVOS</div>
                      <div className="membros-grid">
                        {active.map((m) => (
                          <MemberCard key={m.id} member={m} />
                        ))}
                      </div>
                    </>
                  )}
                  {founders.length > 0 && (
                    <>
                      <div className="membros-section-title">FUNDADORES</div>
                      <div className="membros-grid">
                        {founders.map((m) => (
                          <MemberCard key={m.id} member={m} />
                        ))}
                      </div>
                    </>
                  )}
                  {alumni.length > 0 && (
                    <>
                      <div className="membros-section-title">ALUMNI</div>
                      <div className="membros-grid">
                        {alumni.map((m) => (
                          <MemberCard key={m.id} member={m} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </Window>
        </Reveal>
      </div>
    </section>
  );
}

/* ────────── Footer ────────── */
function FooterSection() {
  return (
    <footer>
      <div className="footer-inner">
        <svg
          className="footer-smiley"
          width="64"
          height="52"
          viewBox="0 0 64 52"
          fill="none"
        >
          <rect x="0" y="8" width="11" height="11" fill="white" />
          <rect x="0" y="26" width="11" height="11" fill="white" />
          <rect x="22" y="0" width="11" height="11" fill="white" />
          <rect x="34" y="10" width="11" height="11" fill="white" />
          <rect x="34" y="22" width="11" height="11" fill="white" />
          <rect x="22" y="32" width="11" height="11" fill="white" />
        </svg>
        <div className="footer-tag">Pro_Gramming</div>
        <div className="footer-message">
          É isso não esqueça de nos seguirem no
          <br />
          <a className="ig" href="https://www.instagram.com/pet.eletrica.ufes" target="_blank" rel="noopener noreferrer">
            instagram
          </a>{" "}
          e acompanhar
          <br />
          nosso{" "}
          <a className="dc" href="https://discord.gg/j4ADvURj " target="_blank" rel="noopener noreferrer">
            discord
          </a>{" "}
          para ficar
          <br />
          por dentro de todas as
          <br />
          novidades e avisos da
          <br />
          Pro_Gramming
        </div>
        <div className="footer-bottom">
          <div className="footer-press">
            Press any key to continue <span className="blink-cursor">_</span>
          </div>
          <div className="footer-dept">
            ENGENHARIA ELÉTRICA
            <br />
            UNIVERSIDADE FEDERAL DO ESPÍRITO SANTO — UFES
            <br />© 2023 PRO_GRAMMING
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ────────── Landing Auth Button ────────── */
function LandingAuthButton({ onLoginClick }: { onLoginClick: () => void }) {
  const { user, profile, signOut } = useAuth();

  if (!user) {
    return (
      <button className="auth-taskbar-btn" onClick={onLoginClick}>
        ENTRAR
      </button>
    );
  }

  const initials = profile?.display_name
    ? profile.display_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

  return (
    <div className="user-badge">
      <Link to="/perfil" className="user-initials" title="Meu perfil">{initials}</Link>
      <span>{profile?.display_name ?? user.email}</span>
      <button className="user-logout" onClick={signOut}>
        SAIR
      </button>
    </div>
  );
}

/* ────────── Landing Inner ────────── */
function LandingInner() {
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();

  // Rola até a seção indicada por #hash (ex: "/#sobre") quando o usuário
  // navega de outra rota. Tenta algumas vezes pois as seções pesadas
  // (membros, edições) podem montar tardiamente.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 20) setTimeout(tryScroll, 100);
    };
    tryScroll();
  }, [location.hash]);

  return (
    <div className="landing-page">
      <Taskbar variant="landing">
        <LandingAuthButton onLoginClick={() => setAuthOpen(true)} />
      </Taskbar>
      <HeroSection />
      <SobreSection />
      <EdicoesSection />
      <MembrosSection />
      <FooterSection />
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}

/* ────────── Landing Page ────────── */
export default function LandingPage() {
  return <LandingInner />;
}
